import { translateBackend } from '@assets/i18n/config';
import type { ConfigurableTablePage } from '@generated/graphql';
import { Settings } from '@mui/icons-material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeIcon from '@mui/icons-material/Home';
import StartIcon from '@mui/icons-material/Start';
import TableChartIcon from '@mui/icons-material/TableChart';
import { Drawer } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import type {
  Section,
  SubSection,
} from '@ovComponents/2-component/navSection/navSection';
import {
  getSubSectionName,
  isNavSubSectionPathActive,
  NavSection,
} from '@ovComponents/2-component/navSection/navSection';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { useGlobalStats } from '@providers/globalStatsHooks';
import { useThemeTokens } from '@providers/themeTokenProvider';
import {
  UserContext,
  useFeatureFlags,
  usePermissions,
} from '@providers/userContextProvider';
import { FlagsmithFlags } from '@utils/flagsmith';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  handleAnalytics,
  sectionsConfig,
  settingsSectionsConfig,
} from './config';
import { useLayoutTokens } from './layout.tokens';

export const useMenuSections = () => {
  const { userContext, activeOrganization } = useContext(UserContext);
  const { permissions } = usePermissions();
  const location = useLocation();
  const { flagEnabled } = useFeatureFlags();
  const repcodeAccessEnabledInFlagsmith =
    activeOrganization.availableFeatureFlags?.includes(
      FlagsmithFlags.repcode_access,
    ) ?? false;

  const hasHomePage =
    userContext.role?.homePageConfiguration &&
    flagEnabled('CONFIGURABLE_HOME_PAGES');

  const dashboardsSection = {
    name: hasHomePage ? 'menu.dashboards' : 'menu.dashboard',
    icon: DashboardIcon,
    subSections:
      userContext.role?.dashboards?.map((x: any) => ({
        name: translateBackend(x.translatedName),
        path: `/dashboard/${x.id}`,
        accessKey: 'HOME',
      })) ?? [],
  };

  const homePageSection = {
    name: 'menu.homePage',
    icon: HomeIcon,
    subSections: [
      {
        name: userContext.role?.homePageConfiguration?.translatedName,
        path: '/',
        accessKey: 'HOME',
      },
    ],
  };

  const settingsSection = {
    name: 'menu.settings',
    icon: Settings,
    newTab: true,
    beta: true,
    subSections: [
      {
        name: 'menu.settings',
        path: '/settings/general',
        newTab: true,
      },
    ],
  };

  const configurableTablePagesSections: Section[] = [];
  if (
    flagEnabled(FlagsmithFlags.configurable_table_pages) &&
    (userContext.role?.configurableTablePages?.length ?? 0) > 0
  ) {
    const configurableTablePages =
      userContext.role?.configurableTablePages ?? [];
    const groupedPages: Record<string, any> = configurableTablePages.reduce(
      (acc: any, page: ConfigurableTablePage) => {
        const sectionName = page.section.name;
        const sectionTranslatedName = translateBackend(sectionName);
        if (!acc[sectionTranslatedName]) {
          acc[sectionTranslatedName] = {
            sectionName,
            icon: page.section.icon || TableChartIcon,
            pages: [],
          };
        }
        acc[sectionTranslatedName].pages.push({
          name: page.name,
          path: `/configurableTablePage/${page.id}`,
          configurableTablePage: true,
          permissions: [
            'read:configurable_table_page',
            'read:configurable_table_data',
          ],
        });
        return acc;
      },
      {},
    );

    // Convert grouped pages to sections based on count
    Object.values(groupedPages).forEach((group: any) => {
      if (group.pages.length === 1) {
        // Single page: use section name as direct menu item
        configurableTablePagesSections.push({
          name: group.sectionName,
          icon: group.icon,
          subSections: [
            {
              name: group.sectionName,
              path: group.pages[0].path,
              permissions: group.pages[0].permissions,
              configurableTablePage: group.pages[0].configurableTablePage,
            },
          ],
        });
      } else {
        // Multiple pages: create expandable section with page names as subsections
        configurableTablePagesSections.push({
          name: group.sectionName,
          icon: group.icon,
          subSections: group.pages,
        });
      }
    });
  }

  let sections = sectionsConfig;

  if (!flagEnabled('ACCOUNT_GROUPS')) {
    sections = sections.map((section) => {
      if (section.name === 'menu.clientManagement') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.accountGroupsList',
          ),
        };
      }
      return section;
    });
  }

  if (!flagEnabled(FlagsmithFlags.recon_flags_dashboard)) {
    sections = sections
      .map((section) => {
        if (section.name !== 'menu.reconciliation') {
          return section;
        }
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.flags',
          ),
        };
      })
      .filter(
        (section) =>
          section.name !== 'menu.reconciliation' ||
          section.subSections.length > 0,
      );
  }

  const defaultSections = [
    dashboardsSection,
    ...sections,
    ...configurableTablePagesSections,
  ];
  if (hasHomePage) {
    defaultSections.unshift(homePageSection);
  }

  if (permissions.includes('write:advanced_organization_settings')) {
    defaultSections.push(settingsSection);
  }
  let settingsSections = settingsSectionsConfig;
  if (!flagEnabled(FlagsmithFlags.recon_flags_dashboard)) {
    settingsSections = settingsSections
      .map((section) => {
        if (section.name !== 'menu.reconciliation') {
          return section;
        }
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.flagPolicies',
          ),
        };
      })
      .filter(
        (section) =>
          section.name !== 'menu.reconciliation' ||
          section.subSections.length > 0,
      );
  }

  if (!flagEnabled('CONFIGURABLE_HOME_PAGES')) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) =>
              subsec.name !== 'menu.homePageConfigurations',
          ),
        };
      }
      return section;
    });
  }

  if (!flagEnabled('TRANSLATED_ATTRIBUTES')) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) =>
              subsec.name !== 'menu.customAttributeLabels',
          ),
        };
      }
      return section;
    });
  }

  if (!repcodeAccessEnabledInFlagsmith) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.accessUsers') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.repCodes',
          ),
        };
      }
      return section;
    });
  }

  if (!flagEnabled(FlagsmithFlags.configurable_table_pages)) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) =>
              subsec.name !== 'menu.configurableTablePages',
          ),
        };
      }
      return section;
    });
  }

  if (!flagEnabled(FlagsmithFlags.form_mapper)) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.formMapper',
          ),
        };
      }
      return section;
    });
  }
  if (!flagEnabled(FlagsmithFlags.alert_banners)) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.alertBanners',
          ),
        };
      }
      return section;
    });
  }

  if (!flagEnabled(FlagsmithFlags.text_variants)) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.theme') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.textDesigner',
          ),
        };
      }
      return section;
    });
  }

  if (
    !activeOrganization.availableFeatureFlags?.includes(
      'CONFIGURABLE_CLIENT_REPORTS',
    )
  ) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.clientReports',
          ),
        };
      }
      return section;
    });
  }

  return location.pathname.includes('/settings')
    ? settingsSections
    : defaultSections;
};

const Sidebar = (props: any) => {
  const { userContext, closed, setClosed } = useContext(UserContext);
  const { sys, comp } = useThemeTokens(useLayoutTokens());
  const { accessiblePages } = usePermissions();
  const globalStats = useGlobalStats();
  const location = useLocation();
  const { window } = props;
  const { mobileOpen } = props;
  const { setMobileOpen } = props;
  const [sectionsOpen, setSectionsOpen] = useState<any[]>([]);
  const [openedMenu, setOpenedMenu] = useState<string>('');
  const { sendAnalytic } = useContext(AnalyticsContext);

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleDrawerClick = (name: string) => {
    handleAnalytics(name, sendAnalytic);
    setMobileOpen(false);
  };

  const finalSections: Section[] = useMenuSections();

  useEffect(() => {
    const initiallyOpenSections: any[] = Array(finalSections.length).fill(
      false,
    );
    finalSections.forEach((sec, idx) => {
      if (sec.subSections && sec.subSections.length > 1) {
        const matchingPaths = sec.subSections.filter((subsec: SubSection) =>
          isNavSubSectionPathActive(subsec, location.pathname),
        );
        if (matchingPaths.length > 0) {
          initiallyOpenSections[idx] = true;
        }
      }
    });
    setSectionsOpen(initiallyOpenSections);
  }, [userContext.role?.dashboards, userContext.role?.configurableTablePages]);

  const calculateActiveSection = () => {
    const activeSection = finalSections.find((section) => {
      if (section.subSections && section.subSections.length > 0) {
        return section.subSections.some((subsec: SubSection) =>
          isNavSubSectionPathActive(subsec, location.pathname),
        );
      }
      return false;
    });

    if (activeSection) {
      const activeSectionIndex = finalSections.indexOf(activeSection);
      const newSectionsOpen = Array(finalSections.length).fill(false);
      newSectionsOpen[activeSectionIndex] = true;
      setSectionsOpen(newSectionsOpen);
    }
  };

  useEffect(() => {
    calculateActiveSection();
  }, [location.pathname, closed]);

  const displayStats = (s: SubSection) =>
    !!(s.stats && globalStats[s.stats] && globalStats[s.stats] > 0);

  const drawer = (
    <Box
      display='flex'
      flexDirection='column'
      justifyContent='space-between'
      sx={{
        height: '100%',
        flexWrap: 'inherit',
        mt: { xs: 2, lg: 0 },
        overflowX: 'visible',
        overflowY: closed ? 'inherit' : 'auto',
      }}
    >
      <Box overflow='visible'>
        {finalSections.map((section, index) => (
          <Box
            key={getSubSectionName(section.name)}
            onMouseEnter={() => setOpenedMenu(getSubSectionName(section.name))}
            overflow='visible'
            data-testid={kebabCase(getSubSectionName(section.name))}
          >
            <NavSection
              section={section}
              accessiblePages={accessiblePages}
              handleDrawerClick={handleDrawerClick}
              globalStats={globalStats}
              displayStats={displayStats}
              showSection={false}
              setSectionsOpen={setSectionsOpen}
              sectionsOpen={sectionsOpen}
              index={index}
              location={location}
              closed={closed && !mobileOpen}
              openedMenu={openedMenu === getSubSectionName(section.name)}
            />
          </Box>
        ))}
      </Box>
      <Box display='flex' justifyContent='end'>
        <IconButton
          onClick={() => setClosed(!closed)}
          size='small'
          sx={{ fontSize: '12px', m: 1 }}
        >
          <StartIcon
            sx={{
              transform: !closed ? 'rotate(180deg)' : undefined,
              transition: 'all 0.4s',
              color: sys.color.onSurface,
            }}
          />
        </IconButton>
      </Box>
    </Box>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box onMouseLeave={() => setOpenedMenu('')}>
      {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
      <Drawer
        container={container}
        variant='temporary'
        open={mobileOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: {
            xs: 'block',
            sm: 'block',
            md: 'block',
            lg: 'none',
          },
          overflow: 'visible',
          '& .MuiDrawer-paper': {
            width: closed && !mobileOpen ? '48px' : '240px',
            backgroundColor: comp.layout.sidebarColor,
            transition: 'all 0.4s',
            overflow: 'visible',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant='permanent'
        sx={{
          display: {
            xs: 'none',
            sm: 'none',
            md: 'none',
            lg: 'block',
          },
          overflow: 'visible',
          '& .MuiDrawer-paper': {
            width: closed && !mobileOpen ? '52px' : '240px',
            backgroundColor: comp.layout.sidebarColor,
            transition: 'all 0.4s',
            overflow: 'visible',
            mt: 8,
            pt: 1.5,
            height: 'calc(100% - 76px)',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
